import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';

export interface ICustomTicketTypeEntityAttributes extends IModelAttributes {
	singularName: string;
	pluralName: string;
	price: number;
	maxCapacity: number;
	quantity: number;
	description: string;

	eventDetailId?: string;
	eventDetail?: Models.EventDetailEntity | Models.IEventDetailEntityAttributes;
	tickets: Array<Models.TicketEntity | Models.ITicketEntityAttributes>;
}
@entity('CustomTicketTypeEntity', 'Custom Ticket Type')
export default class CustomTicketTypeEntity extends Model implements ICustomTicketTypeEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	/**
	 * The name that will display for this passenger ticket pricing when only 1 ticket is selected.
	 */
	@observable
	@attribute<CustomTicketTypeEntity, string>()
	public singularName: string;

	/**
	 * The plural name that will appear for this passenger ticket type.
	 */
	@observable
	@attribute<CustomTicketTypeEntity, string>()
	public pluralName: string;

	/**
	 * The price of this passenger ticket type.
	 */
	@observable
	@attribute<CustomTicketTypeEntity, number>()
	public price: number;

	/**
	 * The quantity of passenger spaces this ticket type will take up.
	 */
	@observable
	@attribute<CustomTicketTypeEntity, number>()
	public maxCapacity: number;

	@observable
	@attribute<CustomTicketTypeEntity, number>()
	public quantity: number;

	/**
	 * A description of the ticket type.
	 */
	@observable
	@attribute<CustomTicketTypeEntity, string>()
	public description: string;

	@observable
	@attribute()
	public eventDetailId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public eventDetail: Models.EventDetailEntity;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public tickets: Models.TicketEntity[] = [];

	constructor(attributes?: Partial<ICustomTicketTypeEntityAttributes>) {
		super(attributes);

		this.tickets = [];

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<ICustomTicketTypeEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.singularName !== undefined) {
				this.singularName = attributes.singularName;
			}
			if (attributes.pluralName !== undefined) {
				this.pluralName = attributes.pluralName;
			}
			if (attributes.price !== undefined) {
				this.price = attributes.price;
			}
			if (attributes.maxCapacity !== undefined) {
				this.maxCapacity = attributes.maxCapacity;
			}
			if (attributes.quantity !== undefined) {
				this.quantity = attributes.quantity;
			}
			if (attributes.description !== undefined) {
				this.description = attributes.description;
			}
			if (attributes.eventDetailId !== undefined) {
				this.eventDetailId = attributes.eventDetailId;
			}
			if (attributes.eventDetail !== undefined) {
				if (attributes.eventDetail === null) {
					this.eventDetail = attributes.eventDetail;
				} else if (attributes.eventDetail instanceof Models.EventDetailEntity) {
					this.eventDetail = attributes.eventDetail;
					this.eventDetailId = attributes.eventDetail.id;
				} else {
					this.eventDetail = new Models.EventDetailEntity(attributes.eventDetail);
					this.eventDetailId = this.eventDetail.id;
				}
			}
			if (attributes.tickets !== undefined && Array.isArray(attributes.tickets)) {
				if (!this.tickets) {
					this.tickets = [];
				}
				for (const model of attributes.tickets) {
					if (model instanceof Models.TicketEntity) {
						this.tickets.push(model);
					} else {
						this.tickets.push(new Models.TicketEntity(model));
					}
				}
			}
		}
	}
}
